import { CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import {
  Container,
  LeftSide,
  Center,
  RightSide,
  IconNavButton,
  NavButton,
  Tabs,
  ContentWrapper,
} from './OrderNavigation.styled';
import { useTranslation } from 'react-i18next';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import Tab from './Tab/Tab';

interface ITabItem {
  id: string;
  label: string;
}

interface ITabsProps {
  tabs: ITabItem[];
  activeTab: string;
  finishedTabs?: string[];
  onNext?: () => void;
  onCreate?: () => void;
  onBack?: () => void;
  onClose?: () => void;
  disabledNext?: boolean;
  disabledBack?: boolean;
  // While an order is on its way: leaving would lose its answer.
  disabledCancel?: boolean;
  loadingInProgress?: boolean;
  enableBackOnFirstTab?: boolean;
  lastTabLabel?: string;
  // Where Back goes when it is not the previous tab, e.g. from the configurator
  // back to the product list on the same tab.
  backDestination?: string;
}

const OrderNavigation = ({
  tabs,
  activeTab,
  finishedTabs,
  onNext,
  onCreate,
  onBack,
  onClose,
  disabledNext,
  disabledBack,
  disabledCancel,
  loadingInProgress,
  enableBackOnFirstTab,
  lastTabLabel,
  backDestination,
}: ITabsProps) => {
  const { t } = useTranslation();
  const { isMidTablet, isPhone, isSmallPhone } = useBreakpointFlag();

  const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
  const isLastTab = activeTabIndex === tabs.length - 1;
  const isFirstTab = activeTabIndex === 0;
  const nextTab = tabs[activeTabIndex + 1];
  const previousTab = tabs[activeTabIndex - 1];

  const isBackDisabled =
    disabledBack || enableBackOnFirstTab || isFirstTab || loadingInProgress;
  const isNextDisabled = disabledNext || loadingInProgress;

  // The three buttons give up their labels in the order they can least afford
  // to keep them: first the tab names Back and Next carry, then Cancel's word,
  // then the two direction words. The finishing action keeps its label at every
  // width - it is what the whole flow leads to, and a bare arrow would not say
  // so.
  const cancelLabel = isPhone ? '' : t('Cancel');
  // Back names where it returns to the same way Next names the tab it leads
  // to, and gives that name up at the same width. On the first tab there is no
  // previous tab to name, and Back is disabled there anyway.
  const backTarget = backDestination ?? previousTab?.label;
  const backLabel = isSmallPhone
    ? ''
    : isMidTablet || !backTarget
    ? t('Back')
    : `${t('Back')}: ${t(backTarget)}`;
  const nextLabel = isLastTab
    ? lastTabLabel ?? t('Finish order')
    : isSmallPhone
    ? ''
    : isMidTablet || !nextTab
    ? t('Next')
    : `${t('Next')}: ${t(nextTab.label)}`;

  const CancelButton = cancelLabel ? NavButton : IconNavButton;
  const BackButton = backLabel ? NavButton : IconNavButton;
  const NextButton = nextLabel ? NavButton : IconNavButton;

  return (
    <Container>
      <ContentWrapper>
        <LeftSide>
          <CancelButton
            redOutline
            label={cancelLabel}
            icon={<X size={16} weight="bold" />}
            pwId="order-navigation-cancel-button"
            disabled={disabledCancel}
            onClick={disabledCancel ? undefined : onClose}
          />
          <BackButton
            secondary
            label={backLabel}
            icon={<CaretLeft size={16} weight="bold" />}
            pwId="order-navigation-back-button"
            disabled={isBackDisabled}
            onClick={isBackDisabled ? undefined : onBack}
          />
        </LeftSide>
        <Center>
          <Tabs>
            {tabs.map((tab, index) => {
              const isActive = tab.id === activeTab;
              const isFinished = finishedTabs?.includes(tab.id);
              return (
                <Tab
                  key={tab.id}
                  active={isActive}
                  finished={isFinished}
                  label={tab.label}
                  index={index}
                />
              );
            })}
          </Tabs>
        </Center>
        <RightSide>
          <NextButton
            primary
            rightIcon
            label={nextLabel}
            icon={<CaretRight size={16} weight="bold" />}
            pwId="order-navigation-next-button"
            disabled={isNextDisabled}
            onClick={isNextDisabled ? undefined : isLastTab ? onCreate : onNext}
          />
        </RightSide>
      </ContentWrapper>
    </Container>
  );
};

export default OrderNavigation;
